import React, { useState, useEffect, useRef } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useToast } from '@/hooks/use-toast';
import { toast as sonnerToast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import api, { API_URL } from '@/utils/api';
import staffService from '../services/staffService';
import schoolsService from '../services/schoolsService';
import gradesService from '../services/gradesService';
import pushNotificationService from '../services/pushNotificationService';
import { usePushNotifications } from '../hooks/usePushNotifications';
import Layout from '../components/Layout';
import DialogModal from '../components/DialogModal';
import LockedFeature from '../components/LockedFeature';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';

import { GearSix, Buildings, Calendar, BookOpen, Users, Bell, BellRinging, Info, Clock, Lock, CurrencyCircleDollar, UploadSimple, ImageSquare, Plus, PencilSimple, Trash } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';

const resolveLogoUrl = (url) => {
  if (!url) return null;
  return /^https?:\/\//i.test(url) ? url : `${API_URL}${url}`;
};

// GES Grading Scale Reference
const GES_SCALE = [
  { grade: 'A', range: '80-100', description: 'Excellent' },
  { grade: 'B', range: '70-79', description: 'Very Good' },
  { grade: 'C', range: '60-69', description: 'Good' },
  { grade: 'D', range: '50-59', description: 'Credit' },
  { grade: 'E', range: '40-49', description: 'Pass' },
  { grade: 'F', range: '0-39', description: 'Fail' }
];

const CLASS_LEVELS = [
  { value: 'kg1', label: 'KG 1' },
  { value: 'kg2', label: 'KG 2' },
  { value: 'primary_1', label: 'Primary 1' },
  { value: 'primary_2', label: 'Primary 2' },
  { value: 'primary_3', label: 'Primary 3' },
  { value: 'primary_4', label: 'Primary 4' },
  { value: 'primary_5', label: 'Primary 5' },
  { value: 'primary_6', label: 'Primary 6' },
  { value: 'jhs_1', label: 'JHS 1' },
  { value: 'jhs_2', label: 'JHS 2' },
  { value: 'jhs_3', label: 'JHS 3' },
];

const emptyBand = () => ({ grade: '', min_score: '', max_score: '', description: '', gpa_point: '' });

const emptySchemeForm = () => ({
  name: '',
  class_level: '',   // '' = every level
  subject_id: '',    // '' = every subject
  bands: [emptyBand()],
});

function SettingsPage() {
  const { t } = useTranslation();
  const { user, hasModule } = useAuth();
  const { toast } = useToast();
  const [school, setSchool] = useState(null);
  const [academicTerms, setAcademicTerms] = useState([]);
  const [academicYears, setAcademicYears] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const logoInputRef = useRef(null);
  const [showTermDialog, setShowTermDialog] = useState(false);
  const [editingTerm, setEditingTerm] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteImpact, setDeleteImpact] = useState(null);
  const [loadingImpact, setLoadingImpact] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [showUserDialog, setShowUserDialog] = useState(false);
  const [gradingSchemes, setGradingSchemes] = useState([]);
  const [gradingSubjects, setGradingSubjects] = useState([]);
  const [loadingSchemes, setLoadingSchemes] = useState(false);
  const [showSchemeDialog, setShowSchemeDialog] = useState(false);
  const [editingScheme, setEditingScheme] = useState(null);
  const [schemeForm, setSchemeForm] = useState(emptySchemeForm());
  const [savingScheme, setSavingScheme] = useState(false);
  const [showDeleteSchemeDialog, setShowDeleteSchemeDialog] = useState(false);
  const [deletingScheme, setDeletingScheme] = useState(null);
  const [emailSettings, setEmailSettings] = useState({
    notifyParents: true,
    notifyTeachers: true,
    notifyFeeReminders: true,
    notifyAttendance: false,
    notifyGrades: false,
    dailyDigest: false
  });
  const [otpSettings, setOtpSettings] = useState({
    is_enabled: true,
    expiry_minutes: 10,
    max_attempts: 3,
    require_for_roles: ['school_admin', 'hr'],
    default_method: 'sms'
  });
  const [payoutDetails, setPayoutDetails] = useState(null);
  const [payoutForm, setPayoutForm] = useState({ account_type: 'mobile_money', bank_code: '', account_number: '' });
  const [payoutBanks, setPayoutBanks] = useState([]);
  const [submittingPayout, setSubmittingPayout] = useState(false);
  const [canteenPayoutDetails, setCanteenPayoutDetails] = useState(null);
  const [canteenPayoutForm, setCanteenPayoutForm] = useState({ account_type: 'mobile_money', bank_code: '', account_number: '' });
  const [submittingCanteenPayout, setSubmittingCanteenPayout] = useState(false);

  // Web push notifications + per-user notification preferences
  const push = usePushNotifications();
  const [pushPreferences, setPushPreferences] = useState(null);
  const [loadingPushPreferences, setLoadingPushPreferences] = useState(true);
  const [savingPushPreference, setSavingPushPreference] = useState(null); // field name currently saving, or null
  const [sendingTestPush, setSendingTestPush] = useState(false);
  const [showUnsubscribeDialog, setShowUnsubscribeDialog] = useState(false);

  const schoolForm = useForm({
    defaultValues: {
      name: '',
      phone: '',
      email: '',
      address: '',
      city: '',
      region: '',
      motto: '',
      logo_url: '',
      require_maker_checker: false,
      require_application_fee: false,
      application_fee_amount: ''
    }
  });

  const termForm = useForm({
    defaultValues: {
      academic_year_id: '',
      academic_year: new Date().getFullYear().toString(),
      term: 'first',
      start_date: '',
      end_date: '',
      is_current: false
    }
  });

  const notificationForm = useForm({
    defaultValues: emailSettings
  });

  const userForm = useForm({
    defaultValues: {
      staff_id: '',
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      date_of_birth: '',
      gender: '',
      staff_type: 'teaching',
      position: '',
      department: '',
      date_joined: '',
      status: 'active'
    }
  });

  // Fetch school and settings data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const schoolData = await schoolsService.getSchool(user.school_id);
        setSchool(schoolData);
        schoolForm.reset({
          name: schoolData.name,
          phone: schoolData.phone,
          email: schoolData.email,
          address: schoolData.address,
          city: schoolData.city,
          region: schoolData.region,
          motto: schoolData.motto || '',
          require_maker_checker: schoolData.require_maker_checker || false,
          require_application_fee: schoolData.require_application_fee || false,
          application_fee_amount: schoolData.application_fee_amount ?? ''
        });

        const termsData = await schoolsService.listTerms(user.school_id);
        setAcademicTerms(termsData);

        const yearsData = await schoolsService.listAcademicYears();
        setAcademicYears(yearsData || []);

        // Mock user data fetch
        const usersData = await staffService.listStaff();
        setUsers(usersData?.items || []);

        loadGradingSchemes();
        gradesService.listSubjects().then(setGradingSubjects).catch(() => setGradingSubjects([]));
      } catch (error) {
        console.error('Error fetching settings:', error);
        toast({
          title: 'Error',
          description: 'Failed to load settings',
          variant: 'destructive'
        });
      } finally {
        setLoading(false);
      }
    };

    if (user?.school_id) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.school_id, toast]);

  // Re-fetch the full terms list from the server — needed because marking one
  // term "current" flips `is_current` on every other term server-side, which
  // a local patch of just the edited term wouldn't reflect.
  const refreshTerms = async () => {
    const termsData = await schoolsService.listTerms(user.school_id);
    setAcademicTerms(termsData);
  };

  // Load OTP settings on mount
  useEffect(() => {
    const loadOTPSettings = async () => {
      try {
        const response = await api.get('/api/auth/admin/otp-settings');
        setOtpSettings({
          is_enabled: response.is_enabled,
          expiry_minutes: response.expiry_minutes,
          max_attempts: response.max_attempts,
          require_for_roles: response.require_for_roles,
          default_method: response.default_method
        });
      } catch (error) {
        console.error('Error loading OTP settings:', error);
        // Keep default settings if API fails
      }
    };
    loadOTPSettings();
  }, []);

  // Load direct-settlement payout details + the bank/MoMo provider list
  useEffect(() => {
    if (!user?.school_id) return;
    schoolsService.getMyPayoutDetails()
      .then((data) => {
        setPayoutDetails(data);
        if (data.payout_account_type) {
          setPayoutForm({
            account_type: data.payout_account_type,
            bank_code: data.payout_bank_code || '',
            account_number: data.payout_account_number || ''
          });
        }
      })
      .catch(() => {});
    staffService.listPayoutBanks().then((data) => setPayoutBanks(data.banks || [])).catch(() => setPayoutBanks([]));
  }, [user?.school_id]);

  const handleSubmitPayoutDetails = async (e) => {
    e.preventDefault();
    if (!payoutForm.bank_code || !payoutForm.account_number) {
      toast({ title: 'Error', description: 'Select a bank/provider and enter an account number', variant: 'destructive' });
      return;
    }
    try {
      setSubmittingPayout(true);
      const result = await schoolsService.submitMyPayoutDetails(payoutForm);
      setPayoutDetails(result);
      toast({ title: 'Submitted', description: `Verified as ${result.payout_account_name} — waiting for a super admin to verify it` });
    } catch (error) {
      toast({ title: 'Error', description: error.message || 'Failed to submit payout details', variant: 'destructive' });
    } finally {
      setSubmittingPayout(false);
    }
  };

  // Load canteen direct-settlement payout details (a second, independent
  // subaccount from the fee-payment one above)
  useEffect(() => {
    if (!user?.school_id) return;
    schoolsService.getMyCanteenPayoutDetails()
      .then((data) => {
        setCanteenPayoutDetails(data);
        if (data.payout_account_type) {
          setCanteenPayoutForm({
            account_type: data.payout_account_type,
            bank_code: data.payout_bank_code || '',
            account_number: data.payout_account_number || ''
          });
        }
      })
      .catch(() => {});
  }, [user?.school_id]);

  const handleSubmitCanteenPayoutDetails = async (e) => {
    e.preventDefault();
    if (!canteenPayoutForm.bank_code || !canteenPayoutForm.account_number) {
      toast({ title: 'Error', description: 'Select a bank/provider and enter an account number', variant: 'destructive' });
      return;
    }
    try {
      setSubmittingCanteenPayout(true);
      const result = await schoolsService.submitMyCanteenPayoutDetails(canteenPayoutForm);
      setCanteenPayoutDetails(result);
      toast({ title: 'Submitted', description: `Verified as ${result.payout_account_name} — waiting for a super admin to verify it` });
    } catch (error) {
      toast({ title: 'Error', description: error.message || 'Failed to submit canteen payout details', variant: 'destructive' });
    } finally {
      setSubmittingCanteenPayout(false);
    }
  };

  // Load per-user notification preferences
  useEffect(() => {
    const loadPushPreferences = async () => {
      try {
        setLoadingPushPreferences(true);
        const prefs = await pushNotificationService.getMyPreferences();
        setPushPreferences(prefs);
      } catch (error) {
        console.error('Error loading notification preferences:', error);
      } finally {
        setLoadingPushPreferences(false);
      }
    };
    loadPushPreferences();
  }, []);

  const handleTogglePushEnabled = async () => {
    try {
      if (push.isSubscribed) {
        setShowUnsubscribeDialog(true);
        return;
      }
      await push.subscribe();
      sonnerToast.success('Push notifications enabled for this device');
    } catch (error) {
      console.error('Error subscribing to push notifications:', error);
      sonnerToast.error(error.message || 'Failed to enable push notifications');
    }
  };

  const handleConfirmUnsubscribe = async () => {
    try {
      await push.unsubscribe();
      sonnerToast.success('Push notifications disabled for this device');
    } catch (error) {
      console.error('Error unsubscribing from push notifications:', error);
      sonnerToast.error(error.message || 'Failed to disable push notifications');
    } finally {
      setShowUnsubscribeDialog(false);
    }
  };

  const handleSendTestPush = async () => {
    try {
      setSendingTestPush(true);
      const result = await pushNotificationService.sendTestPush();
      sonnerToast.success(`Test notification sent to ${result.sent} of ${result.total} device(s)`);
    } catch (error) {
      console.error('Error sending test push notification:', error);
      sonnerToast.error(error.message || 'Failed to send test notification');
    } finally {
      setSendingTestPush(false);
    }
  };

  const handleTogglePushPreference = async (field) => {
    if (!pushPreferences) return;
    const previous = pushPreferences;
    const updated = { ...pushPreferences, [field]: !pushPreferences[field] };
    setPushPreferences(updated);
    try {
      setSavingPushPreference(field);
      const saved = await pushNotificationService.updateMyPreferences({ [field]: updated[field] });
      setPushPreferences(saved);
      sonnerToast.success('Notification preference updated');
    } catch (error) {
      console.error('Error updating notification preference:', error);
      setPushPreferences(previous);
      sonnerToast.error(error.message || 'Failed to update notification preference');
    } finally {
      setSavingPushPreference(null);
    }
  };

  // Handle school profile update
  const handleSchoolUpdate = async (data) => {
    try {
      setSaving(true);
      const payload = {
        ...data,
        application_fee_amount: data.application_fee_amount === '' || data.application_fee_amount === null || data.application_fee_amount === undefined
          ? null
          : Number(data.application_fee_amount),
      };
      const updatedSchool = await schoolsService.updateSchool(user.school_id, payload);
      setSchool(updatedSchool);
      toast({
        title: 'Success',
        description: 'School profile updated successfully'
      });
    } catch (error) {
      console.error('Error updating school:', error);
      toast({
        title: 'Error',
        description: 'Failed to update school profile',
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  // Handle school logo upload — uploads immediately on file selection,
  // independent of the "Save Changes" button for the rest of the school form
  const handleLogoFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setUploadingLogo(true);
      const result = await schoolsService.uploadLogo(user.school_id, file);
      setSchool(prev => ({ ...prev, logo_url: result.logo_url }));
      toast({
        title: 'Success',
        description: 'School logo updated successfully'
      });
    } catch (error) {
      console.error('Error uploading school logo:', error);
      toast({
        title: 'Error',
        description: error?.message || 'Failed to upload logo',
        variant: 'destructive'
      });
    } finally {
      setUploadingLogo(false);
      e.target.value = '';
    }
  };

  // Handle OTP settings save
  const handleSaveOTPSettings = async () => {
    try {
      setSaving(true);
      await api.put('/api/auth/admin/otp-settings', {
        is_enabled: otpSettings.is_enabled,
        expiry_minutes: otpSettings.expiry_minutes,
        max_attempts: otpSettings.max_attempts,
        default_method: otpSettings.default_method,
        require_for_roles: otpSettings.require_for_roles
      });
      
      toast({
        title: 'Success',
        description: 'OTP settings updated successfully'
      });
    } catch (error) {
      console.error('Error saving OTP settings:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to save OTP settings',
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  // Handle add/edit term
  const handleSaveTerm = async (data) => {
    try {
      setSaving(true);
      const payload = { ...data, academic_year_id: data.academic_year_id || null };

      if (editingTerm) {
        await schoolsService.updateTerm(user.school_id, editingTerm.id, payload);
        toast({
          title: 'Success',
          description: 'Academic term updated successfully'
        });
      } else {
        await schoolsService.createTerm(user.school_id, payload);
        toast({
          title: 'Success',
          description: 'Academic term created successfully'
        });
      }
      await refreshTerms();

      setShowTermDialog(false);
      setEditingTerm(null);
      termForm.reset();
    } catch (error) {
      console.error('Error saving term:', error);
      toast({
        title: 'Error',
        description: 'Failed to save academic term',
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  // Fetch what would be affected before showing the delete confirmation
  const handleOpenDeleteTermDialog = async (term) => {
    setEditingTerm(term);
    setShowDeleteDialog(true);
    setDeleteImpact(null);
    setLoadingImpact(true);
    try {
      const impact = await schoolsService.getTermDeleteImpact(user.school_id, term.id);
      setDeleteImpact(impact);
    } catch (error) {
      console.error('Error fetching delete impact:', error);
    } finally {
      setLoadingImpact(false);
    }
  };

  // Handle delete term
  const handleDeleteTerm = async (termId) => {
    try {
      setSaving(true);
      await schoolsService.deleteTerm(user.school_id, termId);
      setAcademicTerms(academicTerms.filter(t => t.id !== termId));
      setShowDeleteDialog(false);
      toast({
        title: 'Success',
        description: 'Academic term deleted successfully'
      });
    } catch (error) {
      console.error('Error deleting term:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete academic term',
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  // Handle notification settings update
  const handleNotificationUpdate = async (data) => {
    try {
      setSaving(true);
      setEmailSettings(data);
      // In a real app, this would be saved to the backend
      toast({
        title: 'Success',
        description: 'Notification settings updated successfully'
      });
    } catch (error) {
      console.error('Error updating notifications:', error);
      toast({
        title: 'Error',
        description: 'Failed to update notification settings',
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  // Handle edit user
  const handleEditUser = (user) => {
    setEditingUser(user);
    userForm.reset({
      staff_id: user.staff_id || '',
      first_name: user.first_name || '',
      last_name: user.last_name || '',
      email: user.email || '',
      phone: user.phone || '',
      date_of_birth: user.date_of_birth || '',
      gender: user.gender || '',
      staff_type: user.staff_type || 'teaching',
      position: user.position || '',
      department: user.department || '',
      date_joined: user.date_joined || '',
      status: user.status || 'active'
    });
    setShowUserDialog(true);
  };

  // Handle save user
  const handleSaveUser = async (data) => {
    try {
      setSaving(true);
      if (editingUser) {
        // Update existing user
        await staffService.updateStaff(editingUser.id, data);
        setUsers(users.map(u => u.id === editingUser.id ? { ...u, ...data } : u));
        toast({
          title: 'Success',
          description: 'User updated successfully'
        });
      } else {
        // Create new user
        const newUser = await staffService.createStaff(data);
        setUsers([...users, newUser]);
        toast({
          title: 'Success',
          description: 'User created successfully'
        });
      }
      setShowUserDialog(false);
      setEditingUser(null);
      userForm.reset();
    } catch (error) {
      console.error('Error saving user:', error);
      toast({
        title: 'Error',
        description: 'Failed to save user',
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleEditTerm = (term) => {
    setEditingTerm(term);
    termForm.reset({
      academic_year_id: term.academic_year_id || '',
      academic_year: term.academic_year,
      term: term.term,
      start_date: term.start_date,
      end_date: term.end_date,
      is_current: term.is_current
    });
    setShowTermDialog(true);
  };

  const handleLockTerm = async (term) => {
    try {
      await schoolsService.lockTerm(user.school_id, term.id);
      await refreshTerms();
      toast({ title: 'Term locked', description: `${term.term} Term - ${term.academic_year} is now locked` });
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to lock term', variant: 'destructive' });
    }
  };

  const handleUnlockTerm = async (term) => {
    try {
      await schoolsService.unlockTerm(user.school_id, term.id);
      await refreshTerms();
      toast({ title: 'Term unlocked', description: `${term.term} Term - ${term.academic_year} can be edited again` });
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to unlock term', variant: 'destructive' });
    }
  };

  const handleCloseTerm = async (term) => {
    try {
      await schoolsService.closeTerm(user.school_id, term.id);
      await refreshTerms();
      toast({ title: 'Term closed', description: `${term.term} Term - ${term.academic_year} is closed and locked` });
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to close term', variant: 'destructive' });
    }
  };

  // ---- Grading schemes (multiple configurable grading systems) ----

  const loadGradingSchemes = async () => {
    setLoadingSchemes(true);
    try {
      const data = await gradesService.listGradingSchemes();
      setGradingSchemes(Array.isArray(data) ? data : []);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to load grading schemes', variant: 'destructive' });
    } finally {
      setLoadingSchemes(false);
    }
  };

  const openCreateSchemeDialog = () => {
    setEditingScheme(null);
    setSchemeForm(emptySchemeForm());
    setShowSchemeDialog(true);
  };

  const openEditSchemeDialog = (scheme) => {
    setEditingScheme(scheme);
    setSchemeForm({
      name: scheme.name,
      class_level: scheme.class_level || '',
      subject_id: scheme.subject_id || '',
      bands: scheme.bands.map((b) => ({
        grade: b.grade, min_score: String(b.min_score), max_score: String(b.max_score),
        description: b.description, gpa_point: String(b.gpa_point),
      })),
    });
    setShowSchemeDialog(true);
  };

  const updateSchemeBand = (index, field, value) => {
    setSchemeForm((prev) => {
      const bands = [...prev.bands];
      bands[index] = { ...bands[index], [field]: value };
      return { ...prev, bands };
    });
  };

  const addSchemeBand = () => {
    setSchemeForm((prev) => ({ ...prev, bands: [...prev.bands, emptyBand()] }));
  };

  const removeSchemeBand = (index) => {
    setSchemeForm((prev) => ({ ...prev, bands: prev.bands.filter((_, i) => i !== index) }));
  };

  const handleSaveScheme = async () => {
    if (!schemeForm.name.trim()) {
      toast({ title: 'Error', description: 'Give this grading scheme a name', variant: 'destructive' });
      return;
    }
    if (schemeForm.bands.some((b) => !b.grade || b.min_score === '' || b.max_score === '' || !b.description || b.gpa_point === '')) {
      toast({ title: 'Error', description: 'Fill in every field for each grade band', variant: 'destructive' });
      return;
    }

    setSavingScheme(true);
    try {
      const payload = {
        name: schemeForm.name.trim(),
        class_level: schemeForm.class_level || null,
        subject_id: schemeForm.subject_id || null,
        bands: schemeForm.bands.map((b) => ({
          grade: b.grade, min_score: parseFloat(b.min_score), max_score: parseFloat(b.max_score),
          description: b.description, gpa_point: parseFloat(b.gpa_point),
        })),
      };
      if (editingScheme) {
        await gradesService.updateGradingScheme(editingScheme.id, payload);
        toast({ title: 'Grading scheme updated' });
      } else {
        await gradesService.createGradingScheme(payload);
        toast({ title: 'Grading scheme created' });
      }
      setShowSchemeDialog(false);
      await loadGradingSchemes();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to save grading scheme', variant: 'destructive' });
    } finally {
      setSavingScheme(false);
    }
  };

  const handleDeleteScheme = async () => {
    if (!deletingScheme) return;
    setSavingScheme(true);
    try {
      await gradesService.deleteGradingScheme(deletingScheme.id);
      toast({ title: 'Grading scheme deleted' });
      setShowDeleteSchemeDialog(false);
      setDeletingScheme(null);
      await loadGradingSchemes();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to delete grading scheme', variant: 'destructive' });
    } finally {
      setSavingScheme(false);
    }
  };

  const schemeScopeLabel = (scheme) => {
    const level = CLASS_LEVELS.find((l) => l.value === scheme.class_level)?.label || 'Every level';
    const subject = gradingSubjects.find((s) => s.id === scheme.subject_id)?.name || 'Every subject';
    return `${level} · ${subject}`;
  };

  if (loading) {
    return (
      <Layout>
        <div className="page" data-testid="settings-page">
          <div className="page-header">
            <div>
              <h1>{t('settings.title')}</h1>
              <p>Loading...</p>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="page" data-testid="settings-page">
        <div className="page-header">
          <div>
            <h1>{t('settings.title')}</h1>
            <p>{t('settings.subtitle')}</p>
          </div>
        </div>

        <Tabs defaultValue="school" className="w-full">
          <TabsList className="grid w-full grid-cols-8">
            <TabsTrigger value="school" className="flex items-center gap-2">
              <Buildings size={18} />
              <span className="hidden sm:inline">School</span>
            </TabsTrigger>
            <TabsTrigger value="terms" className="flex items-center gap-2">
              <Calendar size={18} />
              <span className="hidden sm:inline">Terms</span>
            </TabsTrigger>
            <TabsTrigger value="grades" className="flex items-center gap-2">
              <BookOpen size={18} />
              <span className="hidden sm:inline">Grades</span>
              {!hasModule('academic_reports') && <Lock size={12} weight="fill" />}
            </TabsTrigger>
            <TabsTrigger value="users" className="flex items-center gap-2">
              <Users size={18} />
              <span className="hidden sm:inline">Users</span>
            </TabsTrigger>
            <TabsTrigger value="notifications" className="flex items-center gap-2">
              <Bell size={18} />
              <span className="hidden sm:inline">Alerts</span>
            </TabsTrigger>
            <TabsTrigger value="otp" className="flex items-center gap-2">
              <Lock size={18} />
              <span className="hidden sm:inline">OTP</span>
            </TabsTrigger>
            <TabsTrigger value="payout" className="flex items-center gap-2">
              <CurrencyCircleDollar size={18} />
              <span className="hidden sm:inline">Payout</span>
              {!hasModule('fees_plus') && <Lock size={12} weight="fill" />}
            </TabsTrigger>
            <TabsTrigger value="push" className="flex items-center gap-2">
              <BellRinging size={18} />
              <span className="hidden sm:inline">Push</span>
            </TabsTrigger>
          </TabsList>

          {/* School Profile Tab */}
          <TabsContent value="school" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>School Profile</CardTitle>
                <CardDescription>Manage your school's basic information</CardDescription>
              </CardHeader>
              <CardContent>
                <Form {...schoolForm}>
                  <form onSubmit={schoolForm.handleSubmit(handleSchoolUpdate)} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <FormField
                        control={schoolForm.control}
                        name="name"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>School Name</FormLabel>
                            <FormControl>
                              <Input placeholder="Enter school name" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                  <FormField
                    control={schoolForm.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Email</FormLabel>
                        <FormControl>
                          <Input type="email" placeholder="school@example.com" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={schoolForm.control}
                    name="address"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Address</FormLabel>
                        <FormControl>
                          <Input placeholder="Street address" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                      <FormField
                        control={schoolForm.control}
                        name="city"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>City</FormLabel>
                            <FormControl>
                              <Input placeholder="City" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={schoolForm.control}
                        name="region"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Region</FormLabel>
                            <FormControl>
                              <Input placeholder="Region" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={schoolForm.control}
                        name="motto"
                        render={({ field }) => (
                          <FormItem className="md:col-span-2">
                            <FormLabel>School Motto</FormLabel>
                            <FormControl>
                              <Textarea placeholder="School motto or vision statement" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormItem className="md:col-span-2">
                        <FormLabel>School Logo</FormLabel>
                        <div className="flex items-center gap-4">
                          <div className="w-16 h-16 rounded-lg border border-input bg-muted flex items-center justify-center overflow-hidden shrink-0">
                            {school?.logo_url ? (
                              <img src={resolveLogoUrl(school.logo_url)} alt="School logo" className="w-full h-full object-contain" />
                            ) : (
                              <ImageSquare size={28} className="text-muted-foreground" />
                            )}
                          </div>
                          <div className="flex flex-col gap-2">
                            <input
                              type="file"
                              accept="image/png,image/jpeg,image/webp,image/svg+xml"
                              ref={logoInputRef}
                              onChange={handleLogoFileChange}
                              className="hidden"
                            />
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => logoInputRef.current?.click()}
                              disabled={uploadingLogo}
                            >
                              <UploadSimple size={16} className="mr-2" />
                              {uploadingLogo ? 'Uploading...' : 'Choose Image'}
                            </Button>
                            <p className="text-xs text-muted-foreground">PNG, JPEG, WEBP or SVG, up to 2MB.</p>
                          </div>
                        </div>
                      </FormItem>
                      <FormField
                        control={schoolForm.control}
                        name="require_maker_checker"
                        render={({ field }) => (
                          <FormItem className="flex items-center gap-2 md:col-span-2">
                            <FormControl>
                              <input
                                type="checkbox"
                                checked={field.value}
                                onChange={field.onChange}
                                className="w-4 h-4"
                              />
                            </FormControl>
                            <div>
                              <FormLabel className="cursor-pointer">Require segregation of duties (maker-checker)</FormLabel>
                              <p className="text-sm text-muted-foreground">
                                When enabled, the person who creates a journal entry, expense, stock issuance, or clinic
                                visit can never be the one who approves it — a different staff member must sign off.
                              </p>
                            </div>
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={schoolForm.control}
                        name="require_application_fee"
                        render={({ field }) => (
                          <FormItem className="flex items-center gap-2 md:col-span-2">
                            <FormControl>
                              <input
                                type="checkbox"
                                checked={field.value}
                                onChange={field.onChange}
                                className="w-4 h-4"
                              />
                            </FormControl>
                            <div>
                              <FormLabel className="cursor-pointer">Require an application fee for public admissions</FormLabel>
                              <p className="text-sm text-muted-foreground">
                                When enabled, applicants on your public apply form (/apply/{school?.code}) must pay the
                                amount below via Paystack before their application counts as submitted.
                              </p>
                            </div>
                          </FormItem>
                        )}
                      />
                      {schoolForm.watch('require_application_fee') && (
                        <FormField
                          control={schoolForm.control}
                          name="application_fee_amount"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Application Fee Amount (GHS)</FormLabel>
                              <FormControl>
                                <Input type="number" step="0.01" min="0" placeholder="e.g. 50.00" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      )}
                    </div>
                    <div className="flex justify-end pt-6 border-t">
                      <Button 
                        type="submit" 
                        disabled={saving} 
                        className="px-8 bg-blue-600 hover:bg-blue-700 text-white"
                      >
                        {saving ? 'Updating...' : 'Update'}
                      </Button>
                    </div>
                  </form>
                </Form>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Academic Terms Tab */}
          <TabsContent value="terms" className="space-y-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Academic Terms</CardTitle>
                  <CardDescription>Manage school academic calendar</CardDescription>
                </div>
                <Button 
                  onClick={() => {
                    setEditingTerm(null);
                    termForm.reset();
                    setShowTermDialog(true);
                  }}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  Add Term
                </Button>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {academicTerms.length === 0 ? (
                    <div className="text-center py-8">
                      <Clock size={32} className="mx-auto mb-2 opacity-50" />
                      <p className="text-muted-foreground">No academic terms configured</p>
                    </div>
                  ) : (
                    academicTerms.map(term => (
                      <div key={term.id} className="border rounded-lg p-4 flex justify-between items-start">
                        <div>
                          <div className="flex items-center gap-2">
                            <h4 className="font-semibold capitalize">{term.term} Term - {term.academic_year}</h4>
                            {term.is_current && (
                              <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded">Current</span>
                            )}
                            {term.is_locked && (
                              <span className="bg-slate-200 text-slate-700 text-xs px-2 py-1 rounded flex items-center gap-1">
                                <Lock size={12} /> Locked
                              </span>
                            )}
                            {!term.academic_year_id && (
                              <span className="bg-amber-100 text-amber-800 text-xs px-2 py-1 rounded" title="Not linked to an academic year — won't appear under Academic Calendar">
                                Unlinked
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground">
                            {new Date(term.start_date).toLocaleDateString()} - {new Date(term.end_date).toLocaleDateString()}
                          </p>
                        </div>
                        <div className="flex gap-2 flex-wrap justify-end">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEditTerm(term)}
                            disabled={term.is_locked}
                          >
                            Edit
                          </Button>
                          {term.is_locked ? (
                            <Button variant="outline" size="sm" onClick={() => handleUnlockTerm(term)}>
                              Unlock
                            </Button>
                          ) : (
                            <>
                              <Button variant="outline" size="sm" onClick={() => handleCloseTerm(term)}>
                                Close
                              </Button>
                              <Button variant="outline" size="sm" onClick={() => handleLockTerm(term)}>
                                Lock
                              </Button>
                            </>
                          )}
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => handleOpenDeleteTermDialog(term)}
                            disabled={term.is_locked}
                          >
                            Delete
                          </Button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Grade Scales Tab */}
          <TabsContent value="grades" className="space-y-4">
            <Card>
              <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-3 space-y-0">
                <div className="min-w-0 flex-1">
                  <CardTitle>Custom Grading Schemes</CardTitle>
                  <CardDescription>
                    Set up different grading scales for different class levels or subjects/departments —
                    e.g. a separate scale for Primary vs. JHS, or for Sciences. Any level/subject
                    without a custom scheme here uses the default GES scale below.
                  </CardDescription>
                </div>
                <LockedFeature module="academic_reports">
                  <Button onClick={openCreateSchemeDialog} className="gap-2 shrink-0">
                    <Plus size={16} /> Add Grading Scheme
                  </Button>
                </LockedFeature>
              </CardHeader>
              <CardContent>
                {loadingSchemes ? (
                  <p className="text-sm text-muted-foreground">Loading...</p>
                ) : gradingSchemes.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    No custom grading schemes yet — every level and subject uses the default GES scale below.
                  </p>
                ) : (
                  <div className="space-y-3">
                    {gradingSchemes.map((scheme) => (
                      <div key={scheme.id} className="border rounded-lg p-4">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="font-semibold">{scheme.name}</p>
                            <p className="text-sm text-muted-foreground">{schemeScopeLabel(scheme)}</p>
                          </div>
                          <div className="flex gap-2 shrink-0">
                            <LockedFeature module="academic_reports">
                              <Button variant="outline" size="sm" onClick={() => openEditSchemeDialog(scheme)}>
                                <PencilSimple size={14} />
                              </Button>
                            </LockedFeature>
                            <LockedFeature module="academic_reports">
                              <Button
                                variant="outline" size="sm"
                                onClick={() => { setDeletingScheme(scheme); setShowDeleteSchemeDialog(true); }}
                              >
                                <Trash size={14} />
                              </Button>
                            </LockedFeature>
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-2 mt-3">
                          {scheme.bands.map((b, i) => (
                            <span key={i} className="text-xs border rounded-full px-3 py-1 bg-slate-50">
                              <strong>{b.grade}</strong> ({b.min_score}-{b.max_score}): {b.description}
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Default (GES) Grading Scale</CardTitle>
                <CardDescription>
                  Standard grading scale for Ghana schools — used for any class level or subject
                  that doesn't have a custom grading scheme above.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {GES_SCALE.map((grade, index) => (
                    <div key={index} className="border rounded-lg p-4 bg-gradient-to-br from-slate-50 to-slate-100">
                      <div className="text-3xl font-bold text-primary mb-2">{grade.grade}</div>
                      <p className="text-sm font-semibold text-gray-700">{grade.range}</p>
                      <p className="text-sm text-gray-600 mt-2">{grade.description}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* User Management Tab */}
          <TabsContent value="users" className="space-y-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>User Management & Permissions</CardTitle>
                  <CardDescription>Manage system users and their access levels</CardDescription>
                </div>
                <Button onClick={() => {
                  setEditingUser(null);
                  userForm.reset();
                  setShowUserDialog(true);
                }}>
                  Add User
                </Button>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {users.length === 0 ? (
                    <div className="text-center py-8">
                      <Users size={32} className="mx-auto mb-2 opacity-50" />
                      <p className="text-muted-foreground">No users found</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="border-b">
                          <tr>
                            <th className="text-left py-2 px-4">Name</th>
                            <th className="text-left py-2 px-4">Email</th>
                            <th className="text-left py-2 px-4">Role</th>
                            <th className="text-left py-2 px-4">Status</th>
                            <th className="text-left py-2 px-4">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {users.map(u => (
                            <tr key={u.id} className="border-b">
                              <td className="py-3 px-4">{u.firstname} {u.lastname}</td>
                              <td className="py-3 px-4">{u.email}</td>
                              <td className="py-3 px-4"><span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs">{u.role}</span></td>
                              <td className="py-3 px-4">{u.is_active ? 'Active' : 'Inactive'}</td>
                              <td className="py-3 px-4">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleEditUser(u)}
                                >
                                  Edit
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Notifications Tab */}
          <TabsContent value="notifications" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Notification Settings</CardTitle>
                <CardDescription>Configure email and SMS notifications</CardDescription>
              </CardHeader>
              <CardContent>
                <Form {...notificationForm}>
                  <form onSubmit={notificationForm.handleSubmit(handleNotificationUpdate)} className="space-y-6">
                    <div className="space-y-4">
                      <div className="flex items-center justify-between p-4 border rounded-lg">
                        <div>
                          <h4 className="font-semibold">Parent Notifications</h4>
                          <p className="text-sm text-muted-foreground">Send updates to parents</p>
                        </div>
                        <input
                          type="checkbox"
                          {...notificationForm.register('notifyParents')}
                          className="w-5 h-5"
                        />
                      </div>
                      <div className="flex items-center justify-between p-4 border rounded-lg">
                        <div>
                          <h4 className="font-semibold">Teacher Notifications</h4>
                          <p className="text-sm text-muted-foreground">Send updates to teachers</p>
                        </div>
                        <input
                          type="checkbox"
                          {...notificationForm.register('notifyTeachers')}
                          className="w-5 h-5"
                        />
                      </div>
                      <div className="flex items-center justify-between p-4 border rounded-lg">
                        <div>
                          <h4 className="font-semibold">Fee Reminders</h4>
                          <p className="text-sm text-muted-foreground">Send fee payment reminders</p>
                        </div>
                        <input
                          type="checkbox"
                          {...notificationForm.register('notifyFeeReminders')}
                          className="w-5 h-5"
                        />
                      </div>
                      <div className="flex items-center justify-between p-4 border rounded-lg">
                        <div>
                          <h4 className="font-semibold">Attendance Updates</h4>
                          <p className="text-sm text-muted-foreground">Send daily attendance reports</p>
                        </div>
                        <input
                          type="checkbox"
                          {...notificationForm.register('notifyAttendance')}
                          className="w-5 h-5"
                        />
                      </div>
                      <div className="flex items-center justify-between p-4 border rounded-lg">
                        <div>
                          <h4 className="font-semibold">Grade Updates</h4>
                          <p className="text-sm text-muted-foreground">Send grade notifications</p>
                        </div>
                        <input
                          type="checkbox"
                          {...notificationForm.register('notifyGrades')}
                          className="w-5 h-5"
                        />
                      </div>
                      <div className="flex items-center justify-between p-4 border rounded-lg">
                        <div>
                          <h4 className="font-semibold">Daily Digest</h4>
                          <p className="text-sm text-muted-foreground">Send daily summary email</p>
                        </div>
                        <input
                          type="checkbox"
                          {...notificationForm.register('dailyDigest')}
                          className="w-5 h-5"
                        />
                      </div>
                    </div>
                    <Button type="submit" disabled={saving}>
                      {saving ? 'Saving...' : 'Save Notification Settings'}
                    </Button>
                  </form>
                </Form>
              </CardContent>
            </Card>
          </TabsContent>

          {/* OTP Settings Tab */}
          <TabsContent value="otp" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Two-Factor Authentication (OTP)</CardTitle>
                <CardDescription>Configure OTP settings for school users</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {/* Enable/Disable OTP */}
                  <div className="flex items-center justify-between p-4 border rounded-lg">
                    <div>
                      <h4 className="font-semibold">Enable OTP Authentication</h4>
                      <p className="text-sm text-muted-foreground">Require two-factor authentication for specified roles</p>
                    </div>
                    <input
                      type="checkbox"
                      checked={otpSettings.is_enabled}
                      onChange={(e) => setOtpSettings({...otpSettings, is_enabled: e.target.checked})}
                      className="w-5 h-5 rounded"
                    />
                  </div>

                  {/* Default OTP Method */}
                  <div className="space-y-2">
                    <Label htmlFor="default-method">Default OTP Delivery Method</Label>
                    <Select value={otpSettings.default_method} onValueChange={(value) => setOtpSettings({...otpSettings, default_method: value})}>
                      <SelectTrigger id="default-method">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="email">Email (SMTP)</SelectItem>
                        <SelectItem value="sms">SMS (USMS-GH)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* OTP Expiry Duration */}
                  <div className="space-y-2">
                    <Label htmlFor="expiry-minutes">OTP Code Expiry Duration (minutes)</Label>
                    <Input
                      id="expiry-minutes"
                      type="number"
                      min="1"
                      max="60"
                      value={otpSettings.expiry_minutes}
                      onChange={(e) => setOtpSettings({...otpSettings, expiry_minutes: parseInt(e.target.value) || 10})}
                      className="w-full"
                    />
                    <p className="text-xs text-muted-foreground">How long OTP codes remain valid (default: 10 minutes)</p>
                  </div>

                  {/* Max Attempts */}
                  <div className="space-y-2">
                    <Label htmlFor="max-attempts">Maximum OTP Verification Attempts</Label>
                    <Input
                      id="max-attempts"
                      type="number"
                      min="1"
                      max="10"
                      value={otpSettings.max_attempts}
                      onChange={(e) => setOtpSettings({...otpSettings, max_attempts: parseInt(e.target.value) || 3})}
                      className="w-full"
                    />
                    <p className="text-xs text-muted-foreground">Number of failed attempts before code expires (default: 3)</p>
                  </div>

                  {/* Required Roles */}
                  <div className="space-y-3">
                    <h4 className="font-semibold">Require OTP for these roles</h4>
                    <div className="space-y-2">
                      {[
                        { id: 'school_admin', label: 'School Administrator', description: 'Manages school settings' },
                        { id: 'hr', label: 'HR Manager', description: 'Manages staff and payroll' },
                        { id: 'teacher', label: 'Teacher', description: 'Teaches classes' },
                        { id: 'student', label: 'Student', description: 'Enrolled student' },
                        { id: 'parent', label: 'Parent', description: 'Parent/Guardian' }
                      ].map(role => (
                        <div key={role.id} className="flex items-center justify-between p-3 border rounded-lg">
                          <div>
                            <p className="font-medium">{role.label}</p>
                            <p className="text-xs text-muted-foreground">{role.description}</p>
                          </div>
                          <input
                            type="checkbox"
                            checked={otpSettings.require_for_roles.includes(role.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setOtpSettings({
                                  ...otpSettings,
                                  require_for_roles: [...otpSettings.require_for_roles, role.id]
                                });
                              } else {
                                setOtpSettings({
                                  ...otpSettings,
                                  require_for_roles: otpSettings.require_for_roles.filter(r => r !== role.id)
                                });
                              }
                            }}
                            className="w-5 h-5 rounded"
                          />
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Info Box */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
                    <Info size={20} className="text-blue-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-blue-900 text-sm">OTP Security</h4>
                      <ul className="text-xs text-blue-800 mt-2 space-y-1">
                        <li>• OTP codes are randomly generated 6-digit numbers</li>
                        <li>• Codes expire after the configured duration</li>
                        <li>• Users can resend OTP if not received</li>
                        <li>• Email delivery via SMTP, SMS via USMS-GH</li>
                      </ul>
                    </div>
                  </div>

                  <Button 
                    onClick={handleSaveOTPSettings}
                    disabled={saving} 
                    className="w-full"
                  >
                    {saving ? 'Saving...' : 'Save OTP Settings'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Direct-Settlement Payout Tab */}
          <TabsContent value="payout" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Fee Payout Settlement Details</CardTitle>
                <CardDescription>
                  Where parent fee payments are sent. Submit once — a super admin verifies it,
                  then every payment routes straight to this account automatically, with no separate withdrawal step.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {!hasModule('fees_plus') ? (
                  <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 text-sm text-slate-700">
                    Direct-settlement payout requires the Standard plan or higher. Contact your school administrator to upgrade.
                  </div>
                ) : payoutDetails?.payout_verification_status === 'verified' ? (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <p className="font-semibold text-green-800">Verified ✓</p>
                    <p className="text-sm text-green-900 mt-1">
                      {payoutDetails.payout_account_name} — payments settle here automatically.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {payoutDetails?.payout_verification_status === 'pending' && (
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-900">
                        Submitted as <strong>{payoutDetails.payout_account_name}</strong> — waiting for a super admin to verify it.
                      </div>
                    )}
                    {payoutDetails?.payout_verification_status === 'rejected' && (
                      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-900">
                        Rejected: {payoutDetails.payout_rejection_reason || 'No reason given'} — please resubmit.
                      </div>
                    )}
                    <form onSubmit={handleSubmitPayoutDetails} className="space-y-4">
                      <div className="space-y-2">
                        <Label>Account Type</Label>
                        <Select
                          value={payoutForm.account_type}
                          onValueChange={(value) => setPayoutForm({ ...payoutForm, account_type: value, bank_code: '' })}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="mobile_money">Mobile Money</SelectItem>
                            <SelectItem value="bank">Bank Account</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>{payoutForm.account_type === 'bank' ? 'Bank' : 'Provider'}</Label>
                        <Select
                          value={payoutForm.bank_code}
                          onValueChange={(value) => setPayoutForm({ ...payoutForm, bank_code: value })}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder={`Select ${payoutForm.account_type === 'bank' ? 'bank' : 'provider'}`} />
                          </SelectTrigger>
                          <SelectContent>
                            {payoutBanks
                              .filter((b) => (payoutForm.account_type === 'bank' ? b.type !== 'mobile_money' : b.type === 'mobile_money'))
                              .map((b) => (
                                <SelectItem key={b.code} value={b.code}>{b.name}</SelectItem>
                              ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>{payoutForm.account_type === 'bank' ? 'Account Number' : 'Mobile Money Number'}</Label>
                        <Input
                          value={payoutForm.account_number}
                          onChange={(e) => setPayoutForm({ ...payoutForm, account_number: e.target.value })}
                          placeholder={payoutForm.account_type === 'bank' ? 'Account number' : 'Mobile money number'}
                        />
                      </div>
                      <Button type="submit" disabled={submittingPayout} className="px-8 bg-blue-600 hover:bg-blue-700 text-white">
                        {submittingPayout ? 'Verifying...' : 'Submit for Verification'}
                      </Button>
                    </form>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Canteen Payout Settlement Details</CardTitle>
                <CardDescription>
                  Where canteen top-ups are sent — independent of the fee payout account above, since the canteen
                  is often run against its own bank/MoMo account. Submit once — a super admin verifies it, then
                  every canteen top-up routes straight to this account automatically.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {canteenPayoutDetails?.payout_verification_status === 'verified' ? (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <p className="font-semibold text-green-800">Verified ✓</p>
                    <p className="text-sm text-green-900 mt-1">
                      {canteenPayoutDetails.payout_account_name} — canteen top-ups settle here automatically.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {canteenPayoutDetails?.payout_verification_status === 'pending' && (
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-900">
                        Submitted as <strong>{canteenPayoutDetails.payout_account_name}</strong> — waiting for a super admin to verify it.
                      </div>
                    )}
                    {canteenPayoutDetails?.payout_verification_status === 'rejected' && (
                      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-900">
                        Rejected: {canteenPayoutDetails.payout_rejection_reason || 'No reason given'} — please resubmit.
                      </div>
                    )}
                    <form onSubmit={handleSubmitCanteenPayoutDetails} className="space-y-4">
                      <div className="space-y-2">
                        <Label>Account Type</Label>
                        <Select
                          value={canteenPayoutForm.account_type}
                          onValueChange={(value) => setCanteenPayoutForm({ ...canteenPayoutForm, account_type: value, bank_code: '' })}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="mobile_money">Mobile Money</SelectItem>
                            <SelectItem value="bank">Bank Account</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>{canteenPayoutForm.account_type === 'bank' ? 'Bank' : 'Provider'}</Label>
                        <Select
                          value={canteenPayoutForm.bank_code}
                          onValueChange={(value) => setCanteenPayoutForm({ ...canteenPayoutForm, bank_code: value })}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder={`Select ${canteenPayoutForm.account_type === 'bank' ? 'bank' : 'provider'}`} />
                          </SelectTrigger>
                          <SelectContent>
                            {payoutBanks
                              .filter((b) => (canteenPayoutForm.account_type === 'bank' ? b.type !== 'mobile_money' : b.type === 'mobile_money'))
                              .map((b) => (
                                <SelectItem key={b.code} value={b.code}>{b.name}</SelectItem>
                              ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>{canteenPayoutForm.account_type === 'bank' ? 'Account Number' : 'Mobile Money Number'}</Label>
                        <Input
                          value={canteenPayoutForm.account_number}
                          onChange={(e) => setCanteenPayoutForm({ ...canteenPayoutForm, account_number: e.target.value })}
                          placeholder={canteenPayoutForm.account_type === 'bank' ? 'Account number' : 'Mobile money number'}
                        />
                      </div>
                      <Button type="submit" disabled={submittingCanteenPayout} className="px-8 bg-blue-600 hover:bg-blue-700 text-white">
                        {submittingCanteenPayout ? 'Verifying...' : 'Submit for Verification'}
                      </Button>
                    </form>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Push Notifications Tab — the current user's own settings, visible to everyone (not admin-gated) */}
          <TabsContent value="push" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Push Notifications</CardTitle>
                <CardDescription>Manage push notifications for this device and your notification preferences</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {!push.isSupported ? (
                  <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 text-sm text-slate-700">
                    Push notifications aren't supported in this browser, or require the installed app
                    (they're unavailable in local development since no service worker is registered).
                  </div>
                ) : (
                  <div className="flex items-center justify-between p-4 border rounded-lg">
                    <div>
                      <h4 className="font-semibold">Enable push notifications on this device</h4>
                      <p className="text-sm text-muted-foreground">
                        {push.loading
                          ? 'Checking status...'
                          : push.isSubscribed
                            ? 'This device is subscribed to push notifications.'
                            : 'This device is not subscribed yet.'}
                      </p>
                    </div>
                    <Button
                      variant={push.isSubscribed ? 'outline' : 'default'}
                      onClick={handleTogglePushEnabled}
                      disabled={push.loading || push.subscribing}
                      className={push.isSubscribed ? '' : 'bg-blue-600 hover:bg-blue-700 text-white'}
                    >
                      {push.subscribing ? 'Working...' : push.isSubscribed ? 'Disable' : 'Enable'}
                    </Button>
                  </div>
                )}

                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div>
                    <h4 className="font-semibold">Send test notification</h4>
                    <p className="text-sm text-muted-foreground">Sends a real push notification to every device you've subscribed</p>
                  </div>
                  <Button variant="outline" onClick={handleSendTestPush} disabled={sendingTestPush}>
                    {sendingTestPush ? 'Sending...' : 'Send Test'}
                  </Button>
                </div>

                <div className="space-y-3">
                  <h4 className="font-semibold">Notification preferences</h4>
                  {loadingPushPreferences || !pushPreferences ? (
                    <p className="text-sm text-muted-foreground">Loading preferences...</p>
                  ) : (
                    <div className="space-y-2">
                      {[
                        { field: 'push_new_message', label: 'New messages (push)', description: 'Get a push notification when you receive a new message' },
                        { field: 'push_announcements', label: 'Announcements (push)', description: 'Get a push notification for new announcements' },
                        { field: 'in_app_new_message', label: 'New messages (in-app)', description: 'Show an in-app alert when you receive a new message' },
                        { field: 'in_app_announcements', label: 'Announcements (in-app)', description: 'Show an in-app alert for new announcements' },
                      ].map(({ field, label, description }) => (
                        <div key={field} className="flex items-center justify-between p-3 border rounded-lg">
                          <div>
                            <p className="font-medium">{label}</p>
                            <p className="text-xs text-muted-foreground">{description}</p>
                          </div>
                          <input
                            type="checkbox"
                            checked={!!pushPreferences[field]}
                            onChange={() => handleTogglePushPreference(field)}
                            disabled={savingPushPreference === field}
                            className="w-5 h-5 rounded"
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Unsubscribe from Push Notifications confirmation */}
        <DialogModal
          isOpen={showUnsubscribeDialog}
          onClose={() => setShowUnsubscribeDialog(false)}
          title="Disable push notifications?"
          message="This device will stop receiving push notifications until you enable them again."
          type="warning"
          primaryAction={{ label: 'Disable', onClick: handleConfirmUnsubscribe }}
          secondaryAction={{ label: 'Cancel', onClick: () => setShowUnsubscribeDialog(false) }}
        />

        {/* Term Dialog */}
        <Dialog open={showTermDialog} onOpenChange={setShowTermDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingTerm ? 'Edit Academic Term' : 'Add Academic Term'}</DialogTitle>
              <DialogDescription>
                {editingTerm ? 'Update term details' : 'Create a new academic term'}
              </DialogDescription>
            </DialogHeader>
            <Form {...termForm}>
              <form onSubmit={termForm.handleSubmit(handleSaveTerm)} className="space-y-4">
                <FormField
                  control={termForm.control}
                  name="academic_year"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Academic Year</FormLabel>
                      <FormControl>
                        <Input placeholder="2024" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={termForm.control}
                  name="academic_year_id"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Link to Academic Year</FormLabel>
                      <Select
                        onValueChange={(value) => field.onChange(value === 'none' ? '' : value)}
                        value={field.value || 'none'}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Not linked" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="none">Not linked</SelectItem>
                          {academicYears.map(year => (
                            <SelectItem key={year.id} value={year.id}>{year.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={termForm.control}
                  name="term"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Term</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="first">First Term</SelectItem>
                          <SelectItem value="second">Second Term</SelectItem>
                          <SelectItem value="third">Third Term</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={termForm.control}
                  name="start_date"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Start Date</FormLabel>
                      <FormControl>
                        <Input type="date" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={termForm.control}
                  name="end_date"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>End Date</FormLabel>
                      <FormControl>
                        <Input type="date" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={termForm.control}
                  name="is_current"
                  render={({ field }) => (
                    <FormItem className="flex items-center gap-2">
                      <FormControl>
                        <input
                          type="checkbox"
                          checked={field.value}
                          onChange={field.onChange}
                          className="w-4 h-4"
                        />
                      </FormControl>
                      <FormLabel className="cursor-pointer">Mark as current term</FormLabel>
                    </FormItem>
                  )}
                />
                <div className="flex gap-2 justify-end pt-4">
                  <Button variant="outline" onClick={() => setShowTermDialog(false)}>
                    Cancel
                  </Button>
                  <Button 
                    type="submit" 
                    disabled={saving}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    {saving ? 'Saving...' : 'Save Term'}
                  </Button>
                </div>
              </form>
            </Form>
          </DialogContent>
        </Dialog>

        {/* Delete Dialog */}
        <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete Academic Term</DialogTitle>
              <DialogDescription>
                {editingTerm
                  ? `Delete ${editingTerm.term} Term - ${editingTerm.academic_year}?`
                  : 'Delete this academic term?'}
              </DialogDescription>
            </DialogHeader>

            {loadingImpact && (
              <p className="text-sm text-muted-foreground">Checking what this will affect...</p>
            )}

            {!loadingImpact && deleteImpact && (
              <div className="space-y-3 text-sm">
                {deleteImpact.total_to_be_deleted > 0 ? (
                  <div className="rounded border border-destructive/50 bg-destructive/10 p-3">
                    <p className="font-medium text-destructive">
                      This will permanently delete {deleteImpact.total_to_be_deleted} record{deleteImpact.total_to_be_deleted === 1 ? '' : 's'}:
                    </p>
                    <ul className="list-disc list-inside mt-1">
                      {Object.entries(deleteImpact.will_be_deleted).map(([label, count]) => (
                        <li key={label}>{count} {label}</li>
                      ))}
                    </ul>
                    <p className="mt-1">This cannot be undone and will need to be re-entered if you delete this term.</p>
                  </div>
                ) : (
                  <p className="text-muted-foreground">No grades, attendance, or other academic records are tied to this term yet — safe to delete.</p>
                )}

                {Object.keys(deleteImpact.will_be_preserved || {}).length > 0 && (
                  <div className="rounded border border-border bg-muted/50 p-3">
                    <p className="font-medium">These records will be kept, just unlinked from this term:</p>
                    <ul className="list-disc list-inside mt-1">
                      {Object.entries(deleteImpact.will_be_preserved).map(([label, count]) => (
                        <li key={label}>{count} {label}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => { setShowDeleteDialog(false); setEditingTerm(null); setDeleteImpact(null); }}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={() => {
                  if (editingTerm?.id) {
                    handleDeleteTerm(editingTerm.id);
                    setEditingTerm(null);
                    setDeleteImpact(null);
                  }
                }}
                disabled={saving || loadingImpact}
              >
                {saving ? 'Deleting...' : 'Delete'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* User Edit Dialog */}
        <Dialog open={showUserDialog} onOpenChange={setShowUserDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{editingUser ? 'Edit User' : 'Add User'}</DialogTitle>
              <DialogDescription>
                {editingUser ? 'Update user information' : 'Add a new user to the system'}
              </DialogDescription>
            </DialogHeader>
            <Form {...userForm}>
              <form onSubmit={userForm.handleSubmit(handleSaveUser)} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={userForm.control}
                    name="first_name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>First Name</FormLabel>
                        <FormControl>
                          <Input placeholder="John" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={userForm.control}
                    name="last_name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Last Name</FormLabel>
                        <FormControl>
                          <Input placeholder="Doe" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <FormField
                  control={userForm.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email</FormLabel>
                      <FormControl>
                        <Input type="email" placeholder="john.doe@school.com" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={userForm.control}
                  name="phone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Phone</FormLabel>
                      <FormControl>
                        <Input placeholder="+233 53 448 4781" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={userForm.control}
                  name="staff_id"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Staff ID</FormLabel>
                      <FormControl>
                        <Input placeholder="STF001" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={userForm.control}
                    name="date_of_birth"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Date of Birth</FormLabel>
                        <FormControl>
                          <Input type="date" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={userForm.control}
                    name="gender"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Gender</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="male">Male</SelectItem>
                            <SelectItem value="female">Female</SelectItem>
                            <SelectItem value="other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={userForm.control}
                    name="staff_type"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Staff Type</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="teaching">Teaching Staff</SelectItem>
                            <SelectItem value="non_teaching">Non-Teaching Staff</SelectItem>
                            <SelectItem value="admin">Administrator</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={userForm.control}
                    name="status"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Status</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="active">Active</SelectItem>
                            <SelectItem value="inactive">Inactive</SelectItem>
                            <SelectItem value="suspended">Suspended</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <FormField
                  control={userForm.control}
                  name="position"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Position</FormLabel>
                      <FormControl>
                        <Input placeholder="Mathematics Teacher" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={userForm.control}
                  name="department"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Department</FormLabel>
                      <FormControl>
                        <Input placeholder="Mathematics" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={userForm.control}
                  name="date_joined"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Date Joined</FormLabel>
                      <FormControl>
                        <Input type="date" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <div className="flex gap-2 justify-end pt-4">
                  <Button variant="outline" onClick={() => setShowUserDialog(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white">
                    {saving ? 'Saving...' : 'Save User'}
                  </Button>
                </div>
              </form>
            </Form>
          </DialogContent>
        </Dialog>

        {/* Grading Scheme create/edit dialog */}
        <Dialog open={showSchemeDialog} onOpenChange={setShowSchemeDialog}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingScheme ? 'Edit Grading Scheme' : 'Add Grading Scheme'}</DialogTitle>
              <DialogDescription>
                Define a name, which class level and/or subject it applies to, and its grade bands.
                Leave class level or subject unset to apply it to every level/subject.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="sm:col-span-1">
                  <Label>Name</Label>
                  <Input
                    value={schemeForm.name}
                    onChange={(e) => setSchemeForm((prev) => ({ ...prev, name: e.target.value }))}
                    placeholder="e.g. JHS Grading Scale"
                  />
                </div>
                <div>
                  <Label>Class Level</Label>
                  <Select
                    value={schemeForm.class_level || 'all'}
                    onValueChange={(v) => setSchemeForm((prev) => ({ ...prev, class_level: v === 'all' ? '' : v }))}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Every level</SelectItem>
                      {CLASS_LEVELS.map((l) => (
                        <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Subject</Label>
                  <Select
                    value={schemeForm.subject_id || 'all'}
                    onValueChange={(v) => setSchemeForm((prev) => ({ ...prev, subject_id: v === 'all' ? '' : v }))}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Every subject</SelectItem>
                      {gradingSubjects.map((s) => (
                        <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label>Grade Bands</Label>
                  <Button type="button" variant="outline" size="sm" onClick={addSchemeBand} className="gap-1">
                    <Plus size={14} /> Add Band
                  </Button>
                </div>
                <div className="space-y-2">
                  {schemeForm.bands.map((band, index) => (
                    <div key={index} className="grid grid-cols-12 gap-2 items-center">
                      <Input
                        className="col-span-2" placeholder="Grade" value={band.grade}
                        onChange={(e) => updateSchemeBand(index, 'grade', e.target.value)}
                      />
                      <Input
                        className="col-span-2" type="number" placeholder="Min %" value={band.min_score}
                        onChange={(e) => updateSchemeBand(index, 'min_score', e.target.value)}
                      />
                      <Input
                        className="col-span-2" type="number" placeholder="Max %" value={band.max_score}
                        onChange={(e) => updateSchemeBand(index, 'max_score', e.target.value)}
                      />
                      <Input
                        className="col-span-3" placeholder="Description" value={band.description}
                        onChange={(e) => updateSchemeBand(index, 'description', e.target.value)}
                      />
                      <Input
                        className="col-span-2" type="number" step="0.1" placeholder="GPA" value={band.gpa_point}
                        onChange={(e) => updateSchemeBand(index, 'gpa_point', e.target.value)}
                      />
                      <Button
                        type="button" variant="ghost" size="sm" className="col-span-1"
                        onClick={() => removeSchemeBand(index)}
                        disabled={schemeForm.bands.length === 1}
                      >
                        <Trash size={14} />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex gap-2 justify-end mt-4">
              <Button variant="outline" onClick={() => setShowSchemeDialog(false)} disabled={savingScheme}>
                Cancel
              </Button>
              <Button onClick={handleSaveScheme} disabled={savingScheme}>
                {savingScheme ? 'Saving...' : editingScheme ? 'Save Changes' : 'Create Scheme'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Grading Scheme delete confirm */}
        <Dialog open={showDeleteSchemeDialog} onOpenChange={setShowDeleteSchemeDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete Grading Scheme</DialogTitle>
              <DialogDescription>
                {deletingScheme
                  ? `Delete "${deletingScheme.name}"? Grades already recorded keep whatever letter grade was
                     shown at the time — this only affects future grading.`
                  : 'Delete this grading scheme?'}
              </DialogDescription>
            </DialogHeader>
            <div className="flex gap-2 justify-end">
              <Button
                variant="outline"
                onClick={() => { setShowDeleteSchemeDialog(false); setDeletingScheme(null); }}
                disabled={savingScheme}
              >
                Cancel
              </Button>
              <Button variant="destructive" onClick={handleDeleteScheme} disabled={savingScheme}>
                {savingScheme ? 'Deleting...' : 'Delete'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}

export default SettingsPage;
